import React, { useState, useMemo } from 'react';
import { reportsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { MultiSelect } from './MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Search, ChevronRight, Download, Info, BarChart3, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const usd = (n) =>
  `USD ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const shortDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' });
};

// Opening/Closing are running balances and may legitimately be negative (a net
// credit), so they get a sign-aware colour; Invoiced/Collected never do.
const signColor = (n) => (Number(n) < 0 ? 'text-[#5556A8]' : 'text-[#1C1F1D]');

// Opening/Closing shown on both sides: what students owe (the school's opening
// balance), the credit held for students who paid ahead, and the net the
// Closing formula runs on. owed + credit = net.
const BalanceSides = ({ owed, credit, net, bold }) => (
  <div className="leading-tight">
    <span className={bold ? 'font-bold' : ''}>{usd(owed)}</span>
    <span className="block text-[11px] font-normal text-[#5556A8]">credit {usd(credit)}</span>
    <span className={`block text-[11px] font-normal ${signColor(net)} opacity-70`}>net {usd(net)}</span>
  </div>
);

const toCsv = (rows, columns) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [columns.map((c) => esc(c.label)).join(','),
    ...rows.map((r) => columns.map((c) => esc(c.get(r))).join(','))].join('\n');
};

const downloadBlob = (name, payload, type) => {
  const blob = payload instanceof Blob ? payload : new Blob([payload], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const downloadCsv = (name, csv) => downloadBlob(name, csv, 'text/csv;charset=utf-8;');

const INVOICE_COLUMNS = [
  { label: 'Invoice #', get: (r) => r.invoice_number },
  { label: 'Student ID', get: (r) => r.student_code },
  { label: 'Student', get: (r) => r.student_name },
  { label: 'Branch', get: (r) => r.branch },
  { label: 'Class', get: (r) => r.current_class },
  { label: 'Term', get: (r) => `${r.academic_year} ${r.term}` },
  { label: 'Fee Heads', get: (r) => r.fee_heads },
  { label: 'Amount', get: (r) => r.amount },
  { label: 'Currency', get: (r) => r.currency },
  { label: 'Amount (USD)', get: (r) => r.amount_usd },
  { label: 'Discount', get: (r) => r.discount_amount },
  { label: 'Paid', get: (r) => r.paid_amount },
  { label: 'Outstanding', get: (r) => r.outstanding },
  { label: 'Status', get: (r) => r.status },
  { label: 'Due Date', get: (r) => r.due_date },
];

const COLLECTION_COLUMNS = [
  { label: 'Receipt #', get: (r) => r.receipt_number },
  { label: 'Date', get: (r) => r.date },
  { label: 'Student ID', get: (r) => r.student_code },
  { label: 'Student', get: (r) => r.student_name },
  { label: 'Branch', get: (r) => r.branch },
  { label: 'Class', get: (r) => r.current_class },
  { label: 'Mode', get: (r) => r.payment_mode },
  { label: 'Bank', get: (r) => r.bank_name },
  { label: 'Reference', get: (r) => r.reference_number },
  { label: 'Invoice #', get: (r) => r.invoice_number },
  { label: 'Term', get: (r) => `${r.academic_year} ${r.term}` },
  { label: 'Fee Head', get: (r) => r.fee_head },
  { label: 'Amount', get: (r) => r.amount },
  { label: 'Currency', get: (r) => r.currency },
  { label: 'Amount (USD)', get: (r) => r.amount_usd },
  { label: 'Late Fee?', get: (r) => (r.is_surcharge ? 'Yes' : '') },
  { label: 'Unallocated?', get: (r) => (r.is_unallocated ? 'Yes' : '') },
];

const CREDIT_NOTE_COLUMNS = [
  { label: 'Credit Note #', get: (r) => r.credit_note_number },
  { label: 'Approved On', get: (r) => r.date },
  { label: 'Raised On', get: (r) => r.raised_on },
  { label: 'Student ID', get: (r) => r.student_code },
  { label: 'Student', get: (r) => r.student_name },
  { label: 'Branch', get: (r) => r.branch },
  { label: 'Class', get: (r) => r.current_class },
  { label: 'Invoice #', get: (r) => r.invoice_number || r.invoice_reference },
  { label: 'Invoice Term', get: (r) => r.invoice_term },
  { label: 'Reason', get: (r) => r.reason },
  { label: 'Amount', get: (r) => r.amount },
  { label: 'Currency', get: (r) => r.currency },
  { label: 'Amount (USD)', get: (r) => r.amount_usd },
  { label: 'Raised By', get: (r) => r.raised_by },
  { label: 'Approved By', get: (r) => r.approved_by },
  { label: 'Names Invoice?', get: (r) => (r.linked ? 'Yes' : 'No') },
];

const SIDE_LABEL = { owed: 'Owes', credit: 'In credit', zero: 'Settled' };

// Opening and Closing drill-downs share one shape: one row per student, the
// balance built up from its parts, for reconciling against the school's own
// records line by line. Opening counts what happened BEFORE the term starts,
// Closing everything up to the END of it.
const isBalanceKind = (kind) => kind === 'opening' || kind === 'closing';
const BALANCE_WORDING = {
  opening: { when: 'before', title: 'Opening' },
  closing: { when: 'to end', title: 'Closing' },
};

const balanceColumns = (kind) => {
  const { when, title } = BALANCE_WORDING[kind];
  const w = when.replace(/\b\w/g, (c) => c.toUpperCase());
  return [
    { label: 'Student ID', get: (r) => r.student_code },
    { label: 'Student', get: (r) => r.student_name },
    { label: 'Branch', get: (r) => r.branch },
    { label: 'Class', get: (r) => r.current_class },
    { label: 'Lifecycle', get: (r) => r.lifecycle },
    { label: 'Pre-system Balance', get: (r) => r.pre_system },
    { label: `Invoiced ${w}`, get: (r) => r.invoiced },
    { label: 'Invoices', get: (r) => r.invoice_count },
    { label: `Received ${w}`, get: (r) => -r.received },
    { label: `Unallocated Received ${w}`, get: (r) => -r.unallocated },
    { label: `Credit Notes ${w}`, get: (r) => -r.credit_notes },
    { label: `${title} Balance`, get: (r) => r.balance },
    { label: 'Side', get: (r) => SIDE_LABEL[r.side] },
  ];
};

// Per drill-down kind: dialog title, headline total, row-count label, CSV columns.
const DRILL_KINDS = {
  invoices: { title: 'Term-wise Invoice', totalLabel: 'Total Invoiced', total: (d) => d.total_invoiced,
    countLabel: 'Invoices', columns: INVOICE_COLUMNS, colSpan: 10 },
  collections: { title: 'Term-wise Collection', totalLabel: 'Total Collected', total: (d) => d.total_collected,
    countLabel: 'Allocations', columns: COLLECTION_COLUMNS, colSpan: 9 },
  creditNotes: { title: 'Term-wise Credit Notes', totalLabel: 'Total Credit Notes', total: (d) => d.total_credit_notes,
    countLabel: 'Credit Notes', columns: CREDIT_NOTE_COLUMNS, colSpan: 8 },
  opening: { title: 'Opening Balance — by student', totalLabel: 'Opening (owed)', total: (d) => d.total_owed,
    countLabel: 'Students listed', columns: balanceColumns('opening'), colSpan: 8 },
  closing: { title: 'Closing Balance — by student', totalLabel: 'Closing (owed)', total: (d) => d.total_owed,
    countLabel: 'Students listed', columns: balanceColumns('closing'), colSpan: 8 },
};

/** One KPI tile. `onClick` makes it a drill-down affordance. */
const Kpi = ({ label, value, hint, tone, onClick, testId }) => (
  <div
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
    data-testid={testId}
    className={`p-4 rounded-lg border transition ${tone} ${
      onClick ? 'cursor-pointer hover:shadow-md hover:border-[#3E3F96] focus:outline-none focus:ring-2 focus:ring-[#3E3F96]' : ''
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs text-[#5C635F] uppercase font-semibold">{label}</p>
      {onClick && <ChevronRight className="w-4 h-4 text-[#3E3F96] shrink-0" />}
    </div>
    <p className="text-xl font-bold tabular-nums mt-1">{usd(value)}</p>
    <p className="text-xs text-[#5C635F] mt-1">
      {hint && onClick ? `${hint} · click for breakdown` : (hint || (onClick ? 'Click to drill down' : ''))}
    </p>
  </div>
);

const TermFinancialsReport = ({ configBranches = [], configClasses = [] }) => {
  const { user, isAdmin, userBranches } = useAuth();
  const branchOptions = isAdmin ? configBranches : (userBranches || []);
  // Mirrors the backend's require_roles on the export endpoint.
  const canExport = ['admin', 'manager'].includes(user?.role);

  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [term, setTerm] = useState('all');
  const [fy, setFy] = useState('2026-2027');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // The filters the shown data was actually fetched with — drill-downs must use
  // these, not whatever the dropdowns have been changed to since Submit.
  const [applied, setApplied] = useState(null);

  const [exporting, setExporting] = useState(false);
  const [drill, setDrill] = useState(null);   // {kind, term, label}
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');
  // Opening drill-down only: which side of the balance to list.
  const [drillSide, setDrillSide] = useState('owed');

  const fyOptions = data?.available_fy?.length
    ? data.available_fy
    : ['2024-2025', '2025-2026', '2026-2027'].map((y) => ({ value: y, label: `${y.slice(2, 4)}-${y.slice(7, 9)}` }));

  const submit = async () => {
    setLoading(true);
    const params = {
      fy,
      term,
      ...(branches.length ? { branch: branches.join(',') } : {}),
      ...(classes.length ? { current_class: classes.join(',') } : {}),
    };
    try {
      const res = await reportsApi.termFinancials(params);
      setData(res.data);
      setApplied(params);
      setDrill(null);
      setDrillData(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load term financials');
    } finally {
      setLoading(false);
    }
  };

  // Exports exactly what is on screen — so it uses `applied`, not the dropdowns,
  // which may have been changed since Submit.
  const exportExcel = async () => {
    if (!applied) return;
    setExporting(true);
    try {
      const res = await reportsApi.exportTermFinancialsExcel(applied);
      const tag = `${applied.fy}_${applied.term === 'all' ? 'all-terms' : applied.term.replace(/\s/g, '')}`;
      downloadBlob(`term_financials_${tag}.xlsx`, res.data);
      toast.success('Excel exported');
    } catch (err) {
      toast.error(err?.response?.status === 403
        ? 'Only Admin and Manager can export reports'
        : 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const openDrill = async (kind, drillTerm) => {
    if (!applied) return;
    const termsList = data?.terms || ['Term 1', 'Term 2', 'Term 3'];
    const label = kind === 'opening'
      ? `start of ${applied.fy} ${drillTerm && drillTerm !== 'all' ? drillTerm : termsList[0]}`
      : kind === 'closing'
        ? `end of ${applied.fy} ${drillTerm && drillTerm !== 'all' ? drillTerm : termsList[termsList.length - 1]}`
      : drillTerm && drillTerm !== 'all'
        ? `${applied.fy} ${drillTerm}`
        : `${applied.fy} — all terms`;
    setDrill({ kind, term: drillTerm || 'all', label });
    setDrillSearch('');
    setDrillSide('owed');
    setDrillData(null);
    setDrillLoading(true);
    try {
      const params = { ...applied, term: drillTerm || 'all' };
      const fetcher = {
        invoices: reportsApi.termFinancialsInvoices,
        collections: reportsApi.termFinancialsCollections,
        creditNotes: reportsApi.termFinancialsCreditNotes,
        opening: reportsApi.termFinancialsOpening,
        closing: reportsApi.termFinancialsClosing,
      }[kind];
      const res = await fetcher(params);
      setDrillData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load drill-down');
    } finally {
      setDrillLoading(false);
    }
  };

  const filteredDrillRows = useMemo(() => {
    let rows = drillData?.rows || [];
    if (isBalanceKind(drill?.kind) && drillSide !== 'all') rows = rows.filter((r) => r.side === drillSide);
    const q = drillSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.student_name, r.student_code, r.invoice_number, r.receipt_number, r.branch,
        r.current_class, r.fee_heads, r.fee_head, r.reference_number,
        r.credit_note_number, r.reason, r.invoice_reference, r.raised_by, r.approved_by]
        .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [drillData, drillSearch, drill, drillSide]);

  const totals = data?.totals;
  const rows = data?.rows || [];

  return (
    <div className="space-y-4">
      {/* ---- Filters ---- */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end flex-wrap">
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Branch</Label>
              <MultiSelect
                options={branchOptions}
                selected={branches}
                onChange={setBranches}
                placeholder="All Branches"
                testId="tf-branch"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Class</Label>
              <MultiSelect
                options={configClasses}
                selected={classes}
                onChange={setClasses}
                placeholder="All Classes"
                testId="tf-class"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">Term</Label>
              <Select value={term} onValueChange={setTerm}>
                <SelectTrigger className="w-44" data-testid="tf-term"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Terms</SelectItem>
                  {(data?.terms || ['Term 1', 'Term 2', 'Term 3']).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[#5C635F] uppercase font-semibold">FY (1 Jul – 30 Jun)</Label>
              <Select value={fy} onValueChange={setFy}>
                <SelectTrigger className="w-44" data-testid="tf-fy"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={submit}
              disabled={loading}
              className="bg-[#3E3F96] hover:bg-[#2D2E7A]"
              data-testid="tf-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          </div>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card className="border-[#D8D9E8]">
          <CardContent className="flex flex-col items-center justify-center h-48 text-[#5C635F]">
            <BarChart3 className="w-12 h-12 mb-4 opacity-50" />
            <p>Pick a branch, term and FY, then hit Submit.</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* ---- KPIs ---- */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi
              label="Term-wise Opening Balance (owed)"
              value={totals.opening_owed}
              hint={`${totals.opening_students_owing} students owe · Credit held ${usd(totals.opening_credit)} · Net ${usd(totals.opening)}`}
              tone="bg-[#ECEDF5] border-[#D8D9E8]"
              onClick={() => openDrill('opening', applied?.term)}
              testId="tf-kpi-opening"
            />
            <Kpi
              label="Term-wise Invoice"
              value={totals.invoiced}
              tone="bg-[#3E3F96]/10 border-[#3E3F96]/25"
              onClick={() => openDrill('invoices', applied?.term)}
              testId="tf-kpi-invoiced"
            />
            <Kpi
              label="Term-wise Collection"
              value={totals.collected}
              tone="bg-[#5556A8]/10 border-[#5556A8]/25"
              onClick={() => openDrill('collections', applied?.term)}
              testId="tf-kpi-collected"
            />
            <Kpi
              label="Term-wise Closing Balance (owed)"
              value={totals.closing_owed}
              hint={`${totals.closing_students_owing} students owe · Credit held ${usd(totals.closing_credit)} · Net ${usd(totals.closing)} (= Opening + Invoiced − Collected − Credit Notes)`}
              tone="bg-[#C85A40]/10 border-[#C85A40]/25"
              onClick={() => openDrill('closing', applied?.term)}
              testId="tf-kpi-closing"
            />
          </div>

          {/* ---- Term-wise breakdown ---- */}
          <Card className="border-[#D8D9E8]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Term-wise Breakdown — FY {fyOptions.find((o) => o.value === data.fy)?.label || data.fy}
              </CardTitle>
              <div className="flex gap-2 flex-wrap items-center justify-end">
                <Badge variant="outline" className="text-xs">
                  {data.meta?.branches?.length === branchOptions.length && !branches.length
                    ? 'All branches' : (data.meta?.branches || []).join(', ')}
                </Badge>
                {/* Only shown when a class filter is actually on, so the default
                    header stays as it was. */}
                {data.meta?.classes?.length > 0 && (
                  <Badge variant="outline" className="text-xs" data-testid="tf-class-badge">
                    {data.meta.classes.join(', ')}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">{data.meta?.students_in_scope} students</Badge>
                <Badge variant="outline" className="text-xs">{totals.invoice_count} invoices</Badge>
                {canExport && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportExcel}
                    disabled={exporting}
                    data-testid="tf-export-excel"
                  >
                    {exporting
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <FileSpreadsheet className="w-4 h-4 mr-2" />}
                    Export Excel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      <TableHead className="text-xs uppercase font-semibold">Term</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Opening Balance</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Invoiced</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Collected</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Credit Notes</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Outstanding</TableHead>
                      <TableHead className="text-xs uppercase font-semibold text-right">Closing Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.label} data-testid={`tf-row-${r.term.replace(/\s/g, '')}`}>
                        <TableCell className="font-medium">
                          {r.term}
                          {r.is_import_term && (
                            <Badge variant="outline" className="ml-2 text-[10px]">opening balance term</Badge>
                          )}
                          <span className="block text-xs text-[#5C635F]">
                            {r.invoice_count} invoices · {r.students_with_activity} students
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <button
                            className="text-right hover:underline decoration-[#3E3F96]"
                            onClick={() => openDrill('opening', r.term)}
                            title="See this opening balance student by student"
                            data-testid={`tf-drill-open-${r.term.replace(/\s/g, '')}`}
                          >
                            <BalanceSides owed={r.opening_owed} credit={r.opening_credit} net={r.opening} />
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <button
                            className="text-[#3E3F96] font-medium hover:underline disabled:text-[#5C635F] disabled:no-underline disabled:cursor-default"
                            disabled={!r.invoice_count}
                            onClick={() => openDrill('invoices', r.term)}
                            data-testid={`tf-drill-inv-${r.term.replace(/\s/g, '')}`}
                          >
                            {usd(r.invoiced)}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <button
                            className="text-[#5556A8] font-medium hover:underline disabled:text-[#5C635F] disabled:no-underline disabled:cursor-default"
                            disabled={!r.collected}
                            onClick={() => openDrill('collections', r.term)}
                            data-testid={`tf-drill-col-${r.term.replace(/\s/g, '')}`}
                          >
                            {usd(r.collected)}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <button
                            className="text-[#C85A40] font-medium hover:underline disabled:text-[#5C635F] disabled:font-normal disabled:no-underline disabled:cursor-default"
                            disabled={!r.credit_notes}
                            onClick={() => openDrill('creditNotes', r.term)}
                            data-testid={`tf-drill-cn-${r.term.replace(/\s/g, '')}`}
                          >
                            {usd(r.credit_notes)}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#D99C3B]">{usd(r.outstanding)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <button
                            className="text-right hover:underline decoration-[#3E3F96]"
                            onClick={() => openDrill('closing', r.term)}
                            title="See this closing balance student by student"
                            data-testid={`tf-drill-close-${r.term.replace(/\s/g, '')}`}
                          >
                            <BalanceSides owed={r.closing_owed} credit={r.closing_credit} net={r.closing} bold />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#5C635F]">
                        No terms match this selection
                      </TableCell></TableRow>
                    )}
                    {rows.length > 0 && (
                      <TableRow className="bg-[#F5F5FA] font-bold">
                        <TableCell>TOTAL</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <BalanceSides owed={totals.opening_owed} credit={totals.opening_credit} net={totals.opening} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{usd(totals.invoiced)}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(totals.collected)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <button
                            className="hover:underline disabled:no-underline disabled:cursor-default"
                            disabled={!totals.credit_notes}
                            onClick={() => openDrill('creditNotes', applied?.term)}
                            data-testid="tf-drill-cn-total"
                          >
                            {usd(totals.credit_notes)}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{usd(totals.outstanding)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <BalanceSides owed={totals.closing_owed} credit={totals.closing_credit} net={totals.closing} bold />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* ---- How the figures are built. Non-obvious enough that leaving it out
                  invites the same misreadings this report was built to fix. ---- */}
          <Card className="border-[#D8D9E8] bg-[#F5F5FA]">
            <CardContent className="p-4 text-xs text-[#5C635F] space-y-1">
              <p className="flex items-center gap-2 font-semibold text-[#1C1F1D]">
                <Info className="w-4 h-4" /> How these figures are built
              </p>
              <p><strong>Opening and Closing are shown on both sides.</strong> The headline is what students
                who owe still owe (how the school reports its opening balance); <em>credit</em> is money held for
                students who paid ahead; <em>net</em> = owed + credit, the figure the Closing formula runs on.</p>
              <p><strong>Opening</strong> is the previous term's Closing. The imported pre-system balance
                ({usd(data.meta?.imported_opening_balance)}) sits on <strong>{data.meta?.import_term}</strong>, the
                first term that carries real invoices — not on Term 1.</p>
              <p><strong>Money is booked by its own date.</strong> The academic year runs 1 July – 30 June
                (Term 1 Jul–Sep, Term 2 Oct–Jan, Term 3 Feb–Jun): anything received or approved up to 30 June is the
                old year, anything from 1 July the new one. <strong>Collected</strong> is every receipt in the term it
                was received — arrears, advance payments and money not yet allocated to an invoice alike — so an
                ended term's Closing and the next Opening never change. <strong>Credit Notes</strong> sit in the term
                they were approved. Invoices sit in the term they bill.</p>
              <p><strong>Outstanding</strong> is a snapshot of what is still open on that term's bills.
                <strong> Closing</strong> is a running ledger balance carrying prior terms forward — the two are
                different numbers and will not agree.</p>
              <p>Terms run chronologically: 2025-2026 Term 3 comes before 2026-2027 Term 1. All figures in USD
                at the current exchange rates. FY = academic year.</p>
              {data.meta?.credit_notes_unlinked > 0 && (
                <p>
                  {data.meta.credit_notes_total - data.meta.credit_notes_linked} of {data.meta.credit_notes_total} approved
                  credit notes ({usd(data.meta.credit_notes_unlinked)}) name no invoice (free text such as "Term 3");
                  they are still counted, in the term they were approved.
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ---- Drill-down ---- */}
      {/* Radix keeps the content mounted for the close animation, so `drill` and
          `drillData` must be cleared together — clearing only `drill` renders one
          more frame against a null and throws. */}
      <Dialog
        open={!!drill}
        onOpenChange={(open) => { if (!open) { setDrill(null); setDrillData(null); setDrillSearch(''); } }}
      >
        <DialogContent className="max-w-[95vw] lg:max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {DRILL_KINDS[drill?.kind]?.title} — {drill?.label}
            </DialogTitle>
          </DialogHeader>

          {drillLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-6 h-6 animate-spin text-[#3E3F96]" />
            </div>
          ) : (!drill || !drillData) ? null : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="p-3 rounded-lg bg-[#3E3F96]/10">
                  <p className="text-xs text-[#5C635F] uppercase font-semibold">
                    {DRILL_KINDS[drill.kind].totalLabel}
                  </p>
                  <p className="text-lg font-bold tabular-nums">
                    {usd(DRILL_KINDS[drill.kind].total(drillData))}
                  </p>
                </div>
                {drill.kind === 'creditNotes' && drillData.unlinked_count > 0 && (
                  <div className="p-3 rounded-lg bg-[#C85A40]/10" data-testid="tf-cn-unlinked">
                    <p className="text-xs text-[#5C635F] uppercase font-semibold">
                      Of which name no invoice
                    </p>
                    <p className="text-lg font-bold tabular-nums">
                      {usd(drillData.unlinked_total)}
                      <span className="text-xs font-normal text-[#5C635F]"> · {drillData.unlinked_count}</span>
                    </p>
                  </div>
                )}
                {isBalanceKind(drill.kind) && (
                  <>
                    <div className="p-3 rounded-lg bg-[#5556A8]/10">
                      <p className="text-xs text-[#5C635F] uppercase font-semibold">Credit held</p>
                      <p className="text-lg font-bold tabular-nums">{usd(drillData.total_credit)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[#ECEDF5]">
                      <p className="text-xs text-[#5C635F] uppercase font-semibold">Net</p>
                      <p className="text-lg font-bold tabular-nums">{usd(drillData.total_net)}</p>
                    </div>
                  </>
                )}
                {drill.kind === 'invoices' && (
                  <div className="p-3 rounded-lg bg-[#D99C3B]/10">
                    <p className="text-xs text-[#5C635F] uppercase font-semibold">Still Outstanding</p>
                    <p className="text-lg font-bold tabular-nums">{usd(drillData.total_outstanding)}</p>
                  </div>
                )}
                {drill.kind === 'collections' && drillData.total_surcharge > 0 && (
                  <div className="p-3 rounded-lg bg-[#D99C3B]/10">
                    <p className="text-xs text-[#5C635F] uppercase font-semibold">Late Fee (excluded above)</p>
                    <p className="text-lg font-bold tabular-nums">{usd(drillData.total_surcharge)}</p>
                  </div>
                )}
                <div className="p-3 rounded-lg bg-[#ECEDF5]">
                  <p className="text-xs text-[#5C635F] uppercase font-semibold">
                    {DRILL_KINDS[drill.kind].countLabel}
                  </p>
                  <p className="text-lg font-bold tabular-nums">{drillData.count}</p>
                </div>
              </div>

              {isBalanceKind(drill.kind) && (
                <div className="space-y-2">
                  <p className="text-xs text-[#5C635F]">
                    Net {drill.kind} = Pre-system {usd(drillData.components.pre_system)}
                    {' '}+ Invoiced {BALANCE_WORDING[drill.kind].when} {usd(drillData.components.invoiced)}
                    {' '}− Received {BALANCE_WORDING[drill.kind].when} {usd(drillData.components.received)}
                    {' '}− Unallocated received {BALANCE_WORDING[drill.kind].when} {usd(drillData.components.unallocated)}
                    {' '}− Credit notes {BALANCE_WORDING[drill.kind].when} {usd(drillData.components.credit_notes)}
                    {' '}= <strong>{usd(drillData.total_net)}</strong>
                    {!drillData.pre_system_included && ` (the pre-system balance starts later, so it is not in this ${drill.kind})`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[['owed', `Owes (${drillData.students_owing})`],
                      ['credit', `In credit (${drillData.students_in_credit})`],
                      ['zero', `Settled (${drillData.students_zero})`],
                      ['all', `All (${drillData.count})`]].map(([v, l]) => (
                      <Button key={v} size="sm" variant={drillSide === v ? 'default' : 'outline'}
                        className={drillSide === v ? 'bg-[#3E3F96] hover:bg-[#2D2E7A]' : ''}
                        onClick={() => setDrillSide(v)} data-testid={`tf-open-side-${v}`}>
                        {l}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Fee-head / mode split */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(drillData.by_fee_head || {}).map(([head, amt]) => (
                  <Badge key={head} variant="outline" className="text-xs capitalize">
                    {head.replace(/_/g, ' ')}: {usd(amt)}
                  </Badge>
                ))}
                {Object.entries(drillData.by_payment_mode || {}).map(([mode, amt]) => (
                  <Badge key={mode} className="bg-[#5556A8]/10 text-[#5556A8] text-xs capitalize">
                    {mode.replace(/_/g, ' ')}: {usd(amt)}
                  </Badge>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C635F]" />
                  <Input
                    className="pl-9"
                    placeholder={drill.kind === 'creditNotes'
                      ? 'Search student, credit note, invoice, reason…'
                      : isBalanceKind(drill.kind) ? 'Search student name, ID, class…'
                      : 'Search student, invoice, receipt, class…'}
                    value={drillSearch}
                    onChange={(e) => setDrillSearch(e.target.value)}
                    data-testid="tf-drill-search"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCsv(
                    `${drill.kind}_${drill.label.replace(/[^\w]+/g, '_')}.csv`,
                    toCsv(filteredDrillRows, DRILL_KINDS[drill.kind].columns)
                  )}
                  data-testid="tf-drill-csv"
                >
                  <Download className="w-4 h-4 mr-2" />CSV
                </Button>
              </div>

              {drillData.truncated && (
                <p className="text-xs text-[#C85A40]">
                  Showing the first {drillData.returned} of {drillData.count} rows. The totals above cover all of
                  them; the CSV covers only what is listed.
                </p>
              )}

              <div className="overflow-x-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5FA]">
                      {drill.kind === 'invoices' ? (
                        <>
                          <TableHead className="text-xs uppercase font-semibold">Invoice #</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Class</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Fee Heads</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Paid</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Outstanding</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Status</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Due</TableHead>
                        </>
                      ) : isBalanceKind(drill.kind) ? (
                        <>
                          <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Class</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Pre-system</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Invoiced {BALANCE_WORDING[drill.kind].when}</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Received {BALANCE_WORDING[drill.kind].when}</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Unallocated</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Credit notes</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">{BALANCE_WORDING[drill.kind].title}</TableHead>
                        </>
                      ) : drill.kind === 'creditNotes' ? (
                        <>
                          <TableHead className="text-xs uppercase font-semibold">Credit Note #</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Approved</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Class</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Invoice #</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Reason</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Raised / Approved By</TableHead>
                        </>
                      ) : (
                        <>
                          <TableHead className="text-xs uppercase font-semibold">Receipt #</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Student</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Branch</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Mode</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Invoice #</TableHead>
                          <TableHead className="text-xs uppercase font-semibold">Fee Head</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">Amount</TableHead>
                          <TableHead className="text-xs uppercase font-semibold text-right">USD</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDrillRows.map((r, idx) => (drill.kind === 'invoices' ? (
                      <TableRow key={r.id} data-testid={`tf-inv-row-${idx}`}>
                        <TableCell className="font-medium text-[#3E3F96]">{r.invoice_number}</TableCell>
                        <TableCell>
                          {r.student_name}
                          <span className="block text-xs text-[#5C635F]">{r.student_code}</span>
                        </TableCell>
                        <TableCell className="text-[#5C635F]">{r.branch}</TableCell>
                        <TableCell>{r.current_class}</TableCell>
                        <TableCell className="text-sm max-w-xs truncate" title={r.fee_heads}>{r.fee_heads}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.currency} {r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          {r.currency !== 'USD' && (
                            <span className="block text-xs text-[#5C635F]">{usd(r.amount_usd)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#5556A8]">
                          {r.paid_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">
                          {r.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-xs capitalize">{r.status}</Badge></TableCell>
                        <TableCell className="text-[#5C635F] text-sm">{shortDate(r.due_date)}</TableCell>
                      </TableRow>
                    ) : isBalanceKind(drill.kind) ? (
                      <TableRow key={r.student_id} data-testid={`tf-open-row-${idx}`}>
                        <TableCell>
                          {r.student_name}
                          <span className="block text-xs text-[#5C635F]">
                            {r.student_code} · {r.branch}{r.lifecycle === 'left' ? ' · left' : ''}
                          </span>
                        </TableCell>
                        <TableCell>{r.current_class}</TableCell>
                        <TableCell className="text-right tabular-nums">{usd(r.pre_system)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {usd(r.invoiced)}
                          {r.invoice_count > 0 && <span className="block text-xs text-[#5C635F]">{r.invoice_count} inv.</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-[#5556A8]">{usd(-r.received)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#D99C3B]">{usd(-r.unallocated)}</TableCell>
                        <TableCell className="text-right tabular-nums text-[#C85A40]">{usd(-r.credit_notes)}</TableCell>
                        <TableCell className={`text-right tabular-nums font-bold ${signColor(r.balance)}`}>{usd(r.balance)}</TableCell>
                      </TableRow>
                    ) : drill.kind === 'creditNotes' ? (
                      <TableRow
                        key={r.id}
                        className={r.linked ? '' : 'bg-[#C85A40]/5'}
                        data-testid={`tf-cn-row-${idx}`}
                      >
                        <TableCell className="font-medium text-[#3E3F96]">{r.credit_note_number}</TableCell>
                        <TableCell className="text-[#5C635F] text-sm">{shortDate(r.date)}</TableCell>
                        <TableCell>
                          {r.student_name}
                          <span className="block text-xs text-[#5C635F]">{r.student_code} · {r.branch}</span>
                        </TableCell>
                        <TableCell>{r.current_class}</TableCell>
                        <TableCell className="text-sm">
                          {r.invoice_number || r.invoice_reference || '-'}
                          {r.linked ? (
                            r.invoice_term && <span className="block text-xs text-[#5C635F]">{r.invoice_term}</span>
                          ) : (
                            <Badge variant="outline" className="mt-1 text-[10px] text-[#C85A40] border-[#C85A40]/40">
                              names no invoice
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm max-w-sm whitespace-pre-wrap break-words">{r.reason || '-'}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.currency} {r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          {r.currency !== 'USD' && (
                            <span className="block text-xs text-[#5C635F]">{usd(r.amount_usd)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-[#5C635F]">
                          {r.raised_by || '-'}
                          <span className="block">{r.approved_by ? `✓ ${r.approved_by}` : ''}</span>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={`${r.receipt_number}-${r.invoice_number}-${idx}`} data-testid={`tf-col-row-${idx}`}>
                        <TableCell className="font-medium text-[#3E3F96]">{r.receipt_number}</TableCell>
                        <TableCell className="text-[#5C635F] text-sm">{shortDate(r.date)}</TableCell>
                        <TableCell>
                          {r.student_name}
                          <span className="block text-xs text-[#5C635F]">{r.student_code}</span>
                        </TableCell>
                        <TableCell className="text-[#5C635F]">{r.branch}</TableCell>
                        <TableCell className="capitalize text-sm">
                          {(r.payment_mode || '').replace(/_/g, ' ')}
                          {r.bank_name && <span className="block text-xs text-[#5C635F]">{r.bank_name}</span>}
                        </TableCell>
                        <TableCell className="text-sm">{r.invoice_number}</TableCell>
                        <TableCell className="text-sm">
                          {r.fee_head}
                          {r.is_surcharge && <Badge variant="outline" className="ml-1 text-[10px]">late fee</Badge>}
                          {r.is_unallocated && <Badge variant="outline" className="ml-1 text-[10px] text-[#D99C3B] border-[#D99C3B]/40">to allocate</Badge>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.currency} {r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{usd(r.amount_usd)}</TableCell>
                      </TableRow>
                    )))}
                    {filteredDrillRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={DRILL_KINDS[drill.kind].colSpan} className="text-center py-8 text-[#5C635F]">
                          {drillData.count === 0 ? 'Nothing recorded for this term' : 'No rows match your search'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TermFinancialsReport;
